import React from 'react';
import Card from '../../ui/Card';
import DescriptionPanel from '../../ui/DescriptionPanel';
import { TrendingUpIcon, TrendingDownIcon } from 'lucide-react';
import { PieChart, Pie, Cell, LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
const CostAnalyticsDashboard: React.FC = () => {
  const kpis = [{
    title: 'Material Cost %',
    value: '57.8%',
    change: '+1.2%',
    isPositive: false,
    subtitle: 'Of total revenue'
  }, {
    title: 'Labour Cost %',
    value: '13.7%',
    change: '-0.3%',
    isPositive: true,
    subtitle: 'Of total revenue'
  }, {
    title: 'Overhead Cost %',
    value: '10.2%',
    change: '+0.5%',
    isPositive: false,
    subtitle: 'Of total revenue'
  }];
  // Cost Breakdown Pie Data
  const costBreakdownData = [{
    name: 'Raw Materials',
    value: 5350,
    percentage: 57.8,
    color: '#3b82f6'
  }, {
    name: 'Labour',
    value: 1265,
    percentage: 13.7,
    color: '#10b981'
  }, {
    name: 'Manufacturing OH',
    value: 925,
    percentage: 10.0,
    color: '#f59e0b'
  }, {
    name: 'Logistics',
    value: 485,
    percentage: 5.2,
    color: '#8b5cf6'
  }, {
    name: 'Quality Control',
    value: 342,
    percentage: 3.7,
    color: '#ec4899'
  }, {
    name: 'Energy',
    value: 285,
    percentage: 3.1,
    color: '#ef4444'
  }, {
    name: 'Depreciation',
    value: 341,
    percentage: 3.7,
    color: '#6b7280'
  }, {
    name: 'Other',
    value: 257,
    percentage: 2.8,
    color: '#14b8a6'
  }];
  // Cost Trend Data (12 months)
  const costTrendData = [{
    month: 'Jan',
    material: 442,
    labour: 104,
    overhead: 85,
    total: 631
  }, {
    month: 'Feb',
    material: 448,
    labour: 105,
    overhead: 86,
    total: 639
  }, {
    month: 'Mar',
    material: 455,
    labour: 106,
    overhead: 87,
    total: 648
  }, {
    month: 'Apr',
    material: 438,
    labour: 104,
    overhead: 85,
    total: 627
  }, {
    month: 'May',
    material: 452,
    labour: 106,
    overhead: 88,
    total: 646
  }, {
    month: 'Jun',
    material: 458,
    labour: 107,
    overhead: 89,
    total: 654
  }, {
    month: 'Jul',
    material: 462,
    labour: 107,
    overhead: 89,
    total: 658
  }, {
    month: 'Aug',
    material: 468,
    labour: 108,
    overhead: 90,
    total: 666
  }, {
    month: 'Sep',
    material: 465,
    labour: 107,
    overhead: 89,
    total: 661
  }, {
    month: 'Oct',
    material: 472,
    labour: 109,
    overhead: 91,
    total: 672
  }, {
    month: 'Nov',
    material: 478,
    labour: 110,
    overhead: 92,
    total: 680
  }, {
    month: 'Dec',
    material: 485,
    labour: 111,
    overhead: 93,
    total: 689
  }];
  // Scrap vs Rework Cost Data
  const scrapReworkData = [{
    month: 'Jan',
    scrap: 28,
    rework: 15
  }, {
    month: 'Feb',
    scrap: 26,
    rework: 14
  }, {
    month: 'Mar',
    scrap: 29,
    rework: 16
  }, {
    month: 'Apr',
    scrap: 25,
    rework: 13
  }, {
    month: 'May',
    scrap: 27,
    rework: 15
  }, {
    month: 'Jun',
    scrap: 24,
    rework: 12
  }, {
    month: 'Jul',
    scrap: 26,
    rework: 14
  }, {
    month: 'Aug',
    scrap: 23,
    rework: 11
  }, {
    month: 'Sep',
    scrap: 25,
    rework: 13
  }, {
    month: 'Oct',
    scrap: 22,
    rework: 10
  }, {
    month: 'Nov',
    scrap: 24,
    rework: 12
  }, {
    month: 'Dec',
    scrap: 21,
    rework: 9
  }];
  // Cost Variance Table Data
  const costVarianceData = [{
    category: 'Raw Materials',
    budget: 5200,
    actual: 5350,
    variance: 150,
    variancePercent: 2.9,
    isNegative: true,
    driver: 'Steel price increase'
  }, {
    category: 'Direct Labour',
    budget: 1285,
    actual: 1265,
    variance: -20,
    variancePercent: -1.6,
    isNegative: false,
    driver: 'Efficiency gains'
  }, {
    category: 'Manufacturing Overhead',
    budget: 895,
    actual: 925,
    variance: 30,
    variancePercent: 3.4,
    isNegative: true,
    driver: 'Maintenance costs'
  }, {
    category: 'Logistics & Freight',
    budget: 465,
    actual: 485,
    variance: 20,
    variancePercent: 4.3,
    isNegative: true,
    driver: 'Fuel surcharges'
  }, {
    category: 'Quality Control',
    budget: 350,
    actual: 342,
    variance: -8,
    variancePercent: -2.3,
    isNegative: false,
    driver: 'Process optimization'
  }, {
    category: 'Energy & Utilities',
    budget: 275,
    actual: 285,
    variance: 10,
    variancePercent: 3.6,
    isNegative: true,
    driver: 'Rate hikes'
  }, {
    category: 'Depreciation',
    budget: 341,
    actual: 341,
    variance: 0,
    variancePercent: 0.0,
    isNegative: false,
    driver: 'As planned'
  }, {
    category: 'Other Costs',
    budget: 245,
    actual: 257,
    variance: 12,
    variancePercent: 4.9,
    isNegative: true,
    driver: 'Misc expenses'
  }];
  return <div data-id="element-3840">
      <DescriptionPanel title="Cost Analytics" description="Comprehensive cost structure analysis covering material, labour, and overhead expenses. Monitor cost trends, identify variances, and track scrap and rework costs to drive operational efficiency and margin improvement." insights={['Material costs at 57.8% of revenue - up 1.2% due to steel price inflation', 'Labour efficiency improved - cost reduced to 13.7% from 14.0%', 'Scrap costs trending down - Q4 average at ₹22 Cr vs ₹27 Cr in Q1', 'Total cost variance of ₹194 Cr unfavorable - primarily material driven']} data-id="element-3841" />
      {/* KPIs */}
      <div className="mb-8" data-id="element-3842">
        <h2 className="text-xl font-bold text-gray-900 mb-6" data-id="element-3843">
          Cost Structure Overview
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3844">
          {kpis.map((kpi, index) => <Card key={index} className="p-6 bg-gradient-to-br from-white to-gray-50" data-id="element-3845">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1" data-id="element-3846">
                {kpi.title}
              </div>
              <div className="text-xs text-gray-400 mb-3" data-id="element-3847">{kpi.subtitle}</div>
              <div className="text-4xl font-bold text-gray-900 mb-3" data-id="element-3848">
                {kpi.value}
              </div>
              <div className={`text-sm flex items-center font-semibold ${kpi.isPositive ? 'text-green-600' : 'text-red-600'}`} data-id="element-3849">
                {kpi.isPositive ? <TrendingDownIcon size={16} className="mr-1" data-id="element-3850" /> : <TrendingUpIcon size={16} className="mr-1" data-id="element-3851" />}
                {kpi.change} vs budget
              </div>
            </Card>)}
        </div>
      </div>
      {/* Cost Breakdown Pie and Cost Trend */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-3852">
        {/* Cost Breakdown Pie */}
        <Card className="p-6" data-id="element-3853">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-3854">
            Cost Breakdown - FY24
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-3855">Total Costs: ₹9,250 Cr</p>
          <ResponsiveContainer width="100%" height={350} data-id="element-3856">
            <PieChart data-id="element-3857">
              <Pie data={costBreakdownData} cx="50%" cy="50%" labelLine={false} label={({
              name,
              percentage
            }) => `${name}: ${percentage.toFixed(1)}%`} outerRadius={120} fill="#8884d8" dataKey="value" data-id="element-3858">
                {costBreakdownData.map((entry, index) => <Cell key={`cell-${index}`} fill={entry.color} data-id="element-3859" />)}
              </Pie>
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-3860" />
            </PieChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3861">
            <div className="grid grid-cols-2 gap-2" data-id="element-3862">
              {costBreakdownData.map((item, index) => <div key={index} className="flex items-center text-xs" data-id="element-3863">
                  <div className="w-3 h-3 rounded mr-2" style={{
                backgroundColor: item.color
              }} data-id="element-3864" />
                  <span className="text-gray-700" data-id="element-3865">
                    {item.name}: ₹{item.value} Cr
                  </span>
                </div>)}
            </div>
          </div>
        </Card>
        {/* Cost Trend (12 months) */}
        <Card className="p-6" data-id="element-3866">
          <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-3867">
            Cost Trend - Last 12 Months
          </h3>
          <p className="text-sm text-gray-600 mb-4" data-id="element-3868">
            Monthly cost breakdown (₹ Cr)
          </p>
          <ResponsiveContainer width="100%" height={350} data-id="element-3869">
            <LineChart data={costTrendData} data-id="element-3870">
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-3871" />
              <XAxis dataKey="month" tick={{
              fontSize: 11
            }} data-id="element-3872" />
              <YAxis tick={{
              fontSize: 12
            }} data-id="element-3873" />
              <Tooltip contentStyle={{
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px'
            }} formatter={(value: number) => `₹${value} Cr`} data-id="element-3874" />
              <Legend data-id="element-3875" />
              <Line type="monotone" dataKey="material" stroke="#3b82f6" strokeWidth={3} name="Material" dot={{
              r: 4
            }} data-id="element-3876" />
              <Line type="monotone" dataKey="labour" stroke="#10b981" strokeWidth={3} name="Labour" dot={{
              r: 4
            }} data-id="element-3877" />
              <Line type="monotone" dataKey="overhead" stroke="#f59e0b" strokeWidth={3} name="Overhead" dot={{
              r: 4
            }} data-id="element-3878" />
              <Line type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={2} strokeDasharray="5 5" name="Total" dot={{
              r: 3
            }} data-id="element-3879" />
            </LineChart>
          </ResponsiveContainer>
          <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3880">
            <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-3881">
              <div className="text-center" data-id="element-3882">
                <div className="text-gray-600" data-id="element-3883">Material Avg</div>
                <div className="text-lg font-bold text-blue-600" data-id="element-3884">₹459 Cr</div>
              </div>
              <div className="text-center" data-id="element-3885">
                <div className="text-gray-600" data-id="element-3886">Labour Avg</div>
                <div className="text-lg font-bold text-green-600" data-id="element-3887">₹107 Cr</div>
              </div>
              <div className="text-center" data-id="element-3888">
                <div className="text-gray-600" data-id="element-3889">Overhead Avg</div>
                <div className="text-lg font-bold text-yellow-600" data-id="element-3890">₹89 Cr</div>
              </div>
              <div className="text-center" data-id="element-3891">
                <div className="text-gray-600" data-id="element-3892">Total Avg</div>
                <div className="text-lg font-bold text-purple-600" data-id="element-3893">₹655 Cr</div>
              </div>
            </div>
          </div>
        </Card>
      </div>
      {/* Scrap vs Rework Cost Chart */}
      <Card className="p-6 mb-8" data-id="element-3894">
        <h3 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-3895">
          Scrap vs Rework Cost Analysis
        </h3>
        <p className="text-sm text-gray-600 mb-4" data-id="element-3896">
          Monthly quality-related costs (₹ Cr)
        </p>
        <ResponsiveContainer width="100%" height={350} data-id="element-3897">
          <BarChart data={scrapReworkData} data-id="element-3898">
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" data-id="element-3899" />
            <XAxis dataKey="month" tick={{
            fontSize: 12
          }} data-id="element-3900" />
            <YAxis tick={{
            fontSize: 12
          }} data-id="element-3901" />
            <Tooltip contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px'
          }} formatter={(value: number) => `₹${value} Cr`} data-id="element-3902" />
            <Legend data-id="element-3903" />
            <Bar dataKey="scrap" fill="#ef4444" name="Scrap Cost" data-id="element-3904" />
            <Bar dataKey="rework" fill="#f59e0b" name="Rework Cost" data-id="element-3905" />
          </BarChart>
        </ResponsiveContainer>
        <div className="mt-4 pt-4 border-t border-gray-200" data-id="element-3906">
          <div className="grid grid-cols-4 gap-4 text-sm" data-id="element-3907">
            <div className="text-center" data-id="element-3908">
              <div className="text-gray-600" data-id="element-3909">Total Scrap Cost</div>
              <div className="text-xl font-bold text-red-600" data-id="element-3910">₹300 Cr</div>
            </div>
            <div className="text-center" data-id="element-3911">
              <div className="text-gray-600" data-id="element-3912">Total Rework Cost</div>
              <div className="text-xl font-bold text-orange-600" data-id="element-3913">₹154 Cr</div>
            </div>
            <div className="text-center" data-id="element-3914">
              <div className="text-gray-600" data-id="element-3915">Combined Total</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-3916">₹454 Cr</div>
            </div>
            <div className="text-center" data-id="element-3917">
              <div className="text-gray-600" data-id="element-3918">Trend</div>
              <div className="text-xl font-bold text-green-600 flex items-center justify-center" data-id="element-3919">
                <TrendingDownIcon size={20} className="mr-1" data-id="element-3920" />
                Improving
              </div>
            </div>
          </div>
        </div>
      </Card>
      {/* Cost Variance Table */}
      <Card className="p-6" data-id="element-3921">
        <h3 className="text-lg font-semibold text-gray-800 mb-4" data-id="element-3922">
          Cost Variance Analysis - FY24
        </h3>
        <div className="overflow-x-auto" data-id="element-3923">
          <table className="w-full" data-id="element-3924">
            <thead data-id="element-3925">
              <tr className="border-b-2 border-gray-300" data-id="element-3926">
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3927">
                  Cost Category
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3928">
                  Budget
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3929">
                  Actual
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3930">
                  Variance
                </th>
                <th className="text-right py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3931">
                  Variance %
                </th>
                <th className="text-center py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3932">
                  Status
                </th>
                <th className="text-left py-3 px-4 text-sm font-bold text-gray-700" data-id="element-3933">
                  Key Driver
                </th>
              </tr>
            </thead>
            <tbody data-id="element-3934">
              {costVarianceData.map((row, index) => <tr key={index} className="border-b border-gray-100 hover:bg-gray-50" data-id="element-3935">
                  <td className="py-3 px-4 text-sm font-medium text-gray-800" data-id="element-3936">
                    {row.category}
                  </td>
                  <td className="py-3 px-4 text-sm text-right text-gray-700" data-id="element-3937">
                    ₹{row.budget} Cr
                  </td>
                  <td className="py-3 px-4 text-sm text-right font-semibold text-gray-900" data-id="element-3938">
                    ₹{row.actual} Cr
                  </td>
                  <td className={`py-3 px-4 text-sm text-right font-semibold ${row.isNegative ? 'text-red-600' : 'text-green-600'}`} data-id="element-3939">
                    {row.variance > 0 ? '+' : ''}₹{row.variance} Cr
                  </td>
                  <td className={`py-3 px-4 text-sm text-right font-bold ${row.isNegative ? 'text-red-600' : 'text-green-600'}`} data-id="element-3940">
                    {row.variancePercent > 0 ? '+' : ''}
                    {row.variancePercent.toFixed(1)}%
                  </td>
                  <td className="py-3 px-4 text-center" data-id="element-3941">
                    <span className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${row.isNegative ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'}`} data-id="element-3942">
                      {row.isNegative ? 'Unfavorable' : 'Favorable'}
                    </span>
                  </td>
                  <td className="py-3 px-4 text-sm text-gray-600" data-id="element-3943">
                    {row.driver}
                  </td>
                </tr>)}
              {/* Total Row */}
              <tr className="bg-gray-100 border-t-2 border-gray-300 font-bold" data-id="element-3944">
                <td className="py-3 px-4 text-sm text-gray-900" data-id="element-3945">TOTAL</td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-3946">
                  ₹9,056 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-gray-900" data-id="element-3947">
                  ₹9,250 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-3948">
                  +₹194 Cr
                </td>
                <td className="py-3 px-4 text-sm text-right text-red-600" data-id="element-3949">
                  +2.1%
                </td>
                <td className="py-3 px-4 text-center" data-id="element-3950">
                  <span className="inline-flex px-3 py-1 rounded-full text-xs font-bold bg-red-100 text-red-700" data-id="element-3951">
                    Unfavorable
                  </span>
                </td>
                <td className="py-3 px-4 text-sm text-gray-600" data-id="element-3952">
                  Material inflation
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div className="mt-6 pt-4 border-t border-gray-200" data-id="element-3953">
          <div className="grid grid-cols-5 gap-4 text-sm" data-id="element-3954">
            <div className="text-center" data-id="element-3955">
              <div className="text-gray-600" data-id="element-3956">Budget Accuracy</div>
              <div className="text-xl font-bold text-gray-900" data-id="element-3957">97.9%</div>
            </div>
            <div className="text-center" data-id="element-3958">
              <div className="text-gray-600" data-id="element-3959">Favorable Variance</div>
              <div className="text-xl font-bold text-green-600" data-id="element-3960">₹28 Cr</div>
            </div>
            <div className="text-center" data-id="element-3961">
              <div className="text-gray-600" data-id="element-3962">Unfavorable Variance</div>
              <div className="text-xl font-bold text-red-600" data-id="element-3963">₹222 Cr</div>
            </div>
            <div className="text-center" data-id="element-3964">
              <div className="text-gray-600" data-id="element-3965">Net Variance</div>
              <div className="text-xl font-bold text-red-600" data-id="element-3966">₹194 Cr</div>
            </div>
            <div className="text-center" data-id="element-3967">
              <div className="text-gray-600" data-id="element-3968">Key Focus Area</div>
              <div className="text-xl font-bold text-blue-600" data-id="element-3969">Materials</div>
            </div>
          </div>
        </div>
      </Card>
    </div>;
};
export default CostAnalyticsDashboard;